import {
  T,
  always,
  and,
  complement,
  cond,
  either,
  equals,
  isEmpty,
  isNil,
  pathSatisfies,
  pipe,
  pluck,
} from 'ramda'
import { getStateFromZip } from 'sidecar-utility-library'
import { getZipFromLatLon } from 'sidecar-utilities'

import { types } from './index'
import { getPolicyInfo } from './utils'

/**
 * Sets the searchLocation to be the location that the map is currently
 * displaying to the user. This is called when the user has changed the maps
 * location or zoom level.
 *
 * @param {object} location The location object from our map component
 */
export const setSearchToMapLoc = location => async (dispatch, getState) => {
  const { Location, SearchFilters } = getState()
  const {
    filters: { searchLocation },
  } = SearchFilters
  const {
    currentLocation: { entity: currentLocation },
  } = Location

  const { lat, lon, radius: rad, zoom } = location
  const { lat: sLat, lon: sLon, radius: sRad = null } = searchLocation
  const { lat: cLat, lon: cLon, radius: cRad = null } = currentLocation
  const latMatch = !!lat && !!sLat && _shortNum(lat) === _shortNum(sLat)
  const lonMatch = !!lon && !!sLon && _shortNum(lon) === _shortNum(sLon)
  const radMatch = !!rad && !!sRad && _shortNum(rad) === _shortNum(sRad)

  // if everything matches, there's no need to update the store
  // and cause the search to be made again.
  if (latMatch && lonMatch && radMatch) return false

  // if we've been given a location, use that, otherwise, fallback
  // to using the currentLocation
  const geoloc = await getZipFromLatLon(lat || cLat, lon || cLon)
  const locIsCurrent = and(
    equals(_shortNum(lat), _shortNum(cLat)),
    equals(_shortNum(lon), _shortNum(cLon)),
  )
  const address = locIsCurrent ? 'Current location' : geoloc.zip
  const viewLocation = {
    ...geoloc,
    radius: rad || cRad,
    address,
    zoom,
  }

  dispatch({
    type: types.UPDATE_SEARCH_FILTERS,
    payload: {
      searchLocation: viewLocation,
    },
  })
}

/**
 * Looks at all of the locations that we consider the user to be in and then
 * picks the one that is both preferred and available. Not all locations are
 * available all the time. The best location we have gets stored as our search
 * location.
 */
export const setSearchToBestLoc = () => async (dispatch, getState) => {
  const { Location, Member, SearchFilters } = getState()

  const locations = pipe(pluck('entity'))(Location)
  const { currentLocation, ipLocation, coverageLocation, mapLocation } =
    locations

  const exists = pathSatisfies(complement(either(isNil, isEmpty)))
  const bestLocation = cond([
    // mapLocation has a zoom and radius by default so is not totally empty
    // only use the map location if we have a lat/lon (having lon means we have both)
    [exists(['mapLocation', 'lat']), always(mapLocation)],
    [exists(['currentLocation']), always(currentLocation)],
    [exists(['ipLocation']), always(ipLocation)],
    [exists(['coverageLocation']), always(coverageLocation)],
    [T, always(coverageLocation)],
  ])(locations)

  const { policyAddress, searchingAs } = getPolicyInfo(Member, SearchFilters)

  // We will use the address associated with the policy as our default. We keep
  // the previous logic which used the users ip address to set the default location
  // as a fallback just in case we don't have a policyAddress for some reason.
  if (!isEmpty(policyAddress)) {
    return dispatch({
      type: types.UPDATE_SEARCH_FILTERS,
      payload: {
        searchLocation: policyAddress,
        searchingAs,
      },
    })
    // this is unlikely to be empty, but just in case
    // don't update searchLocation to be empty.
  } else if (!isEmpty(bestLocation)) {
    return dispatch({
      type: types.UPDATE_SEARCH_FILTERS,
      payload: {
        searchLocation: bestLocation,
      },
    })
  }
}

/**
 * Sets the search location to the users current location, effectively moving
 * the map back to where they started.
 *
 * @param {object} props Props of the calling component
 */
export const setSearchToCurrentLoc = () => async (dispatch, getState) => {
  const { Location } = getState()
  const {
    currentLocation: { entity: currentLoc },
  } = Location
  if (!currentLoc.state && !currentLoc.region && currentLoc.zip) {
    currentLoc.state = await getStateFromZip(currentLoc.zip)
  }

  dispatch({
    type: types.UPDATE_SEARCH_FILTERS,
    payload: {
      searchLocation: currentLoc,
    },
  })
}

/**
 * Sets the search location to the users current location, effectively moving
 * the map back to where they started.
 *
 * @param {object} props Props of the calling component
 */
export const setSearchToStaticLoc = locationKey => (dispatch, getState) => {
  const { Location } = getState()
  const {
    [locationKey]: { entity: staticLoc },
  } = Location

  dispatch({
    type: types.UPDATE_SEARCH_FILTERS,
    payload: {
      searchLocation: staticLoc,
    },
  })
}

// quick util for above method because it's
// easier to compare standardized numbers.
function _shortNum(number) {
  return Number(number).toFixed(2)
}

export default (state, { type }) => {
  switch (type) {
    default:
      return state
  }
}
